import { computed, toValue } from 'vue'
import { defineStore } from 'pinia'
import Project from '@/classes/projects/Project'
import Projects from '@/classes/projects/Projects'
import Contributions from '@/classes/contributions/Contributions'
import PublicProcedureExecutions from '@/classes/procedure_executions/PublicProcedureExecutions'
import Statistics from '@/classes/statistics/Statistics'
import ProjectQuery from '@/classes/queries/ProjectQuery'
import Users from '@/classes/users/Users'
import { pick as _pick } from 'lodash'

export const useProjectStore = defineStore('ProjectStore', {
  state: () => ({
    projects: {},
    project: {},
    statistics: {},
    public_procedure_executions: {},
    contributions: {},
    invites: {},
    invite: {},
    members: {},
    loader: false,
    error: {}
  }),
  getters: {
    getProjects: state => ({ query, dependencies, multi_currency = true } = {}) => state.$getItems({
      resource: 'projects',
      query: computed(() => new ProjectQuery(toValue(query)).to_database),
      dependencies,
      multi_currency,
      fetch: () => state.fetchProjects({ query })
    }),
    getProject: state => ({ id, query, dependencies, multi_currency = true }) => state.$getItem({
      key: id,
      resource: 'project',
      query,
      dependencies,
      multi_currency,
      fetch: () => state.fetchProject({ id, query, multi_currency })
    }),
    getStatistics: state => ({ id, query, dependencies, multi_currency = true }) => state.$getItem({
      resource: 'statistics',
      key: id,
      query,
      dependencies,
      multi_currency,
      fetch: () => state.fetchStatistics({ id, query, multi_currency })
    }),
    getPublicProcedureExecutions: state => ({ id, query, multi_currency = true } = {}) => state.$getItems({
      resource: 'public_procedure_executions',
      query,
      multi_currency,
      fetch: () => state.fetchPublicProcedureExecutions({ id, query, multi_currency })
    }),
    getContributions: state => ({ id, query } = {}) => state.$getItems({
      resource: 'contributions',
      query,
      fetch: () => state.fetchContributions({ id, query })
    }),
    getInvites: state => ({ project_id, query, dependencies }) => state.$getItem({
      resource: 'invites',
      key: project_id,
      dependencies,
      fetch: () => state.fetchInvites({ project_id, query })
    }),
    getInvite: state => ({ id, token, dependencies }) => state.$getItem({
      resource: 'invite',
      key: token,
      dependencies,
      fetch: () => state.fetchInvite({ id, token })
    }),
    getProjectMembers: state => ({ project_id }) => state.$getItem({
      resource: 'members',
      key: project_id,
      fetch: () => state.fetchProjectMembers({ project_id })
    })
  },
  actions: {
    fetchProjects ({ query, multi_currency = true }) {
      return this.$fetchItems({
        endpoint: '/api/v1/projects',
        resource: 'projects',
        model: Projects,
        query: new ProjectQuery(toValue(query)).to_database,
        multi_currency
      })
    },
    async fetchProject ({ id, query, multi_currency = true }) {
      await this.$pipeline({ dependencies: [id] })

      return this.$fetchItem({
        endpoint: `/api/v1/projects/${toValue(id)}`,
        resource: `project.${toValue(id)}`,
        model: Project,
        query,
        multi_currency
      })
    },
    async fetchStatistics ({ id, query, multi_currency = true }) {
      await this.$pipeline({ dependencies: [id] })

      return this.$fetchItem({
        endpoint: `/api/v1/projects/${toValue(id)}/statistics`,
        resource: `statistics.${toValue(id)}`,
        model: Statistics,
        query,
        multi_currency
      })
    },
    async patchProject ({ id, params }) {
      await this.$pipeline({ dependencies: [id] })

      return this.$patchItem({
        endpoint: `/api/v1/projects/${toValue(id)}`,
        resource: `project.${toValue(id)}`,
        model: Project,
        params: {
          project: _pick(new Project(params), Object.keys(params))
        }
      })
    },
    async patchProjectImage ({ id, params }) {
      await this.$pipeline({ dependencies: [id] })

      return this.$patchItem({
        endpoint: `/api/v1/projects/${toValue(id)}`,
        resource: `project.${toValue(id)}`,
        model: Project,
        params,
        config: { headers: { 'Content-Type': 'multipart/form-data' } }
      })
    },
    async deleteProject ({ id }) {
      await this.$pipeline({ dependencies: [id] })

      return this.$deleteItem({
        endpoint: `/api/v1/projects/${toValue(id)}`
      })
    },
    async fetchPublicProcedureExecutions ({ id, query, multi_currency = true }) {
      await this.$pipeline({ dependencies: [id] })

      return this.$fetchItems({
        endpoint: `/api/v1/projects/${toValue(id)}/public_procedure_executions`,
        resource: 'public_procedure_executions',
        model: PublicProcedureExecutions,
        query,
        multi_currency
      })
    },
    async fetchContributions ({ id, query }) {
      await this.$pipeline({ dependencies: [id] })

      return this.$fetchItems({
        endpoint: `/api/v1/projects/${toValue(id)}/contributions`,
        resource: 'contributions',
        model: Contributions,
        query
      })
    },
    async fetchInvites ({ project_id, query }) {
      await this.$pipeline({ dependencies: [project_id] })

      return this.$fetchItem({
        endpoint: `/api/v1/projects/${toValue(project_id)}/invites`,
        resource: `invites.${toValue(project_id)}`,
        query
      })
    },
    async fetchInvite ({ id, token }) {
      await this.$pipeline({ dependencies: [id, token] })

      return this.$fetchItem({
        endpoint: `/api/v1/projects/${toValue(id)}/invites/${toValue(token)}`,
        resource: `invite.${toValue(token)}`
      })
    },
    async fetchContributionsExport ({ id, query, filename }) {
      await this.$pipeline({ dependencies: [id] })

      return this.$fetchExport({
        endpoint: `/api/v1/projects/${toValue(id)}/contributions/download`,
        filename,
        query
      })
    },
    async fetchProjectMembers ({ project_id }) {
      await this.$pipeline({ dependencies: [project_id] })

      return this.$fetchItem({
        endpoint: `/api/v1/projects/${toValue(project_id)}/members`,
        resource: `members.${toValue(project_id)}`,
        model: Users
      })
    },
    async postProjectInvites ({ project_id, invites }) {
      await this.$pipeline({ dependencies: [project_id] })

      return this.$postItem({
        endpoint: `/api/v1/projects/${toValue(project_id)}/invites`,
        resource: `invites.${toValue(project_id)}`,
        params: {
          invites
        }
      })
    },
    async postProjectMembersOwner ({ project_id, user_id }) {
      await this.$pipeline({ dependencies: [project_id, user_id] })

      return this.$postItem({
        endpoint: `/api/v1/projects/${project_id}/members/${user_id}/owner`,
        resource: `members.${project_id}`,
        model: Users
      })
    },
    async deleteProjectInvite ({ project_id, invite_id }) {
      await this.$pipeline({ dependencies: [project_id, invite_id] })

      return this.$deleteItem({
        endpoint: `/api/v1/projects/${toValue(project_id)}/invites/${toValue(invite_id)}`
      })
    },
    async deleteProjectMember ({ project_id, user_id }) {
      await this.$pipeline({ dependencies: [project_id, user_id] })

      return this.$deleteItem({
        endpoint: `/api/v1/projects/${toValue(project_id)}/members/${toValue(user_id)}`
      })
    }
  }
})